// Constants
export const TOUR_TYPES = {
  ONBOARDING: "onboarding",
};

const ROUTES = {
  DASHBOARD: "/dashboard",
  HOME: "/",
  LITERATURE: "/literature/*",
  SEARCH: "/search",
  PROFILE: "/profile",
};

const SECTIONS = {
  INTRO: "intro",
  DASHBOARD: "dashboard",
  TRANSITION: "transition",
  OPAC: "opac",
  NAVIGATION: "navigation",
  PROFILE: "profile",
  COMPLETION: "completion",
};

const SELECTORS = {
  DASHBOARD: {
    ABOUT: '[data-tour="dashboard-about"]',
    SERVICES: '[data-tour="dashboard-services"]',
    ANNOUNCEMENTS: '[data-tour="dashboard-post-announcement"]',
    TOP_BOOKS: '[data-tour="top-borrowed-books"]',
    LIBRARY_NEWS: '[data-tour="library-announcements"]',
    TOP_BORROWERS: '[data-tour="top-borrowers"]',
    CALENDAR: '[data-tour="dashboard-calendar"]',
  },
  OPAC: {
    SEARCH_BAR: '[data-tour="home-search-bar"]',
    QUICK_BUTTONS: '[data-tour="quick-buttons"]',
    FEATURED_BOOK: '[data-tour="featured-book"]',
    BOOK_DETAILS: '[data-tour="book-details"]',
    BOOK_METADATA: '[data-tour="book-metadata-details"]',
    BOOK_LOCATION: '[data-tour="book-location-details"]',
    LOAN_BUTTON: '[data-tour="loan-request-button"]',
    SEARCH_LINK: '[data-tour="literature-search-link"] a[href*="search"]',
    SEARCH_CONTROLS: '[data-tour="search-controls"]',
    SEARCH_FILTERS: '[data-tour="search-filters"]',
    DOCUMENT_LINK: '[data-tour="document-card-link"] a[href*="/literature/"]',
  },
  PROFILE: {
    TAB: '[data-tour="profile-tab"], .profile-tab-item',
    CONTENT: '[data-tour="profile-content"]',
    SETTINGS_TAB:
      '[data-tour="account-settings-tab"], .account-settings-tab-item',
    SETTINGS_CONTENT: '[data-tour="account-settings-content"]',
    NOTIFICATIONS_TAB:
      '[data-tour="notifications-tab"], .notifications-tab-item',
    NOTIFICATIONS_CONTENT: '[data-tour="notifications-content"]',
    ACTIVITY_TAB: '[data-tour="activity-tab"], .activity-tab-item',
    ACTIVITY_OVERVIEW: '[data-tour="activity-overview"]',
    ACTIVITY_SUBTABS: '[data-tour="activity-subtabs"]',
    CURRENT_LOANS: "#patron-current-loans",
  },
  COMMON: {
    BODY: "body",
    MENU_PROFILE: '[data-tour="ils-menu-profile"]',
    FOOTER_FEEDBACK: '[data-tour="footer-feedback"]',
    FOOTER_INFO: '[data-tour="footer-info"]',
    USER_GUIDE: '[data-tour="user-guide"]',
  },
};

const COMMON_ACTIONS = {
  CLICK_TAB: "click_tab",
  CLICK_ELEMENT: "click_element",
  NAVIGATE: "navigate",
};

const BASE_STEP = {
  disableBeacon: true,
};

// Helper function to create step
const createStep = (config) => ({
  ...BASE_STEP,
  ...config,
});

// Main Onboarding Tour Steps
export const onboardingTourSteps = [
  // Welcome
  createStep({
    target: SELECTORS.COMMON.BODY,
    content:
      "Welcome to the library system! Let's take a tour of all the features available to you. We'll start with the dashboard, then explore the catalog, and finish with your profile.",
    title: "Welcome to Your Library System!",
    placement: "center",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.INTRO,
  }),

  // Dashboard Section
  createStep({
    target: SELECTORS.DASHBOARD.ABOUT,
    content:
      "Here you can find information about the library, including policies, and resources.",
    title: "About Section",
    placement: "bottom",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.SERVICES,
    content:
      "Access our library services including OPAC (catalog search) and electronic resources.",
    title: "Library Services",
    placement: "bottom",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.ANNOUNCEMENTS,
    content: "Click here to view library announcements and important updates.",
    title: "Announcements",
    placement: "left",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.TOP_BOOKS,
    content:
      "Discover the most popular books currently borrowed by other patrons.",
    title: "Popular Books",
    placement: "left",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.LIBRARY_NEWS,
    content: "Stay updated with the latest library news and announcements.",
    title: "Library Announcements",
    placement: "right",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.TOP_BORROWERS,
    content: "See who our most active readers are this month.",
    title: "Top Borrowers",
    placement: "right",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),
  createStep({
    target: SELECTORS.DASHBOARD.CALENDAR,
    content:
      "Click here to view library events, holidays, and important dates in our calendar.",
    title: "Library Calendar",
    placement: "left",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
    action: {
      type: COMMON_ACTIONS.CLICK_TAB,
      selector: SELECTORS.DASHBOARD.CALENDAR,
      delay: 800,
    },
  }),
  createStep({
    target: SELECTORS.COMMON.BODY,
    content:
      "The calendar shows all library events, holidays, and important academic dates for the year. You can click on any event to view its details.",
    title: "Calendar Overview",
    placement: "bottom",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.DASHBOARD,
  }),

  // Transition to OPAC
  createStep({
    target: SELECTORS.COMMON.BODY,
    content:
      "Great! You've completed the Dashboard section. Now let's explore the Library Catalog where you can search for books and materials.",
    title: "Moving to Library Catalog",
    placement: "top",
    route: ROUTES.DASHBOARD,
    section: SECTIONS.TRANSITION,
    action: {
      type: COMMON_ACTIONS.NAVIGATE,
      url: ROUTES.HOME,
      waitFor: SELECTORS.OPAC.SEARCH_BAR,
    },
  }),

  // OPAC Home Page
  createStep({
    target: SELECTORS.OPAC.SEARCH_BAR,
    content:
      "Start your search here to find books, journals, and other materials in our collection. You can also access your search history here.",
    title: "Catalog Search",
    placement: "bottom",
    hidePrev: true,
    route: ROUTES.HOME,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.QUICK_BUTTONS,
    content:
      "Use these quick search buttons for popular categories or recent collections.",
    title: "Quick Search Options",
    placement: "bottom",
    route: ROUTES.HOME,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.FEATURED_BOOK,
    content:
      "These are featured books in the catalog. Let's explore a book's details.",
    title: "Featured Books",
    placement: "right",
    route: ROUTES.HOME,
    section: SECTIONS.OPAC,
    action: {
      type: COMMON_ACTIONS.CLICK_ELEMENT,
      selector: SELECTORS.OPAC.DOCUMENT_LINK,
      waitFor: SELECTORS.OPAC.BOOK_DETAILS,
    },
  }),

  // Literature Detail Page
  createStep({
    target: SELECTORS.OPAC.BOOK_DETAILS,
    content:
      "Here you can see detailed information about the book title, author, abstract, and more.",
    title: "Document Details",
    placement: "bottom",
    hidePrev: true,
    route: ROUTES.LITERATURE,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.BOOK_METADATA,
    content:
      "Here you can see detailed information about the book details and contents.",
    title: "Document Metadata",
    placement: "bottom",
    route: ROUTES.LITERATURE,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.BOOK_LOCATION,
    content:
      "Here you can see detailed information about the book including physical locations, availability, description, and more.",
    title: "Document Location Details",
    placement: "bottom",
    route: ROUTES.LITERATURE,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.LOAN_BUTTON,
    content:
      "Here you can find the borrower's slip form. Fill out and submit to request a document for borrowing.",
    title: "Request a Loan",
    placement: "bottom",
    route: ROUTES.LITERATURE,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.SEARCH_LINK,
    content:
      "Now let's continue to explore the search features in the catalog.",
    title: "Literature Search",
    placement: "top",
    route: ROUTES.LITERATURE,
    section: SECTIONS.OPAC,
    action: {
      type: COMMON_ACTIONS.CLICK_ELEMENT,
      selector: SELECTORS.OPAC.SEARCH_LINK,
      waitFor: SELECTORS.OPAC.SEARCH_CONTROLS,
    },
  }),

  // Search Page
  createStep({
    target: SELECTORS.OPAC.SEARCH_CONTROLS,
    content:
      "Sort your results and control how many items are displayed per page. Switch between list and grid view to see results in your preferred format.",
    title: "Sort & Display Options",
    placement: "bottom",
    hidePrev: true,
    route: ROUTES.SEARCH,
    section: SECTIONS.OPAC,
  }),
  createStep({
    target: SELECTORS.OPAC.SEARCH_FILTERS,
    content:
      "Use these filters to narrow down your search results by format, types, availability, and more.",
    title: "Search Filters",
    placement: "top",
    route: ROUTES.SEARCH,
    section: SECTIONS.OPAC,
  }),

  // Navigation Menu
  createStep({
    target: SELECTORS.COMMON.MENU_PROFILE,
    content: "Navigate to profile, dashboard page, and sign out options.",
    title: "Navigation Menu",
    placement: "bottom",
    route: ROUTES.SEARCH,
    section: SECTIONS.NAVIGATION,
  }),

  // Transition to Profile
  createStep({
    target: SELECTORS.COMMON.BODY,
    content:
      "Excellent! You've learned how to search the catalog. Finally, let's explore your Profile where you can manage your account and track your library activity.",
    title: "Moving to Your Profile page",
    placement: "top",
    route: ROUTES.SEARCH,
    section: SECTIONS.TRANSITION,
    action: {
      type: COMMON_ACTIONS.NAVIGATE,
      url: ROUTES.PROFILE,
      waitFor: SELECTORS.PROFILE.TAB,
    },
  }),

  // Profile Section
  createStep({
    target: SELECTORS.PROFILE.TAB,
    content:
      "This is your profile tab where you can view your personal information.",
    title: "Profile Information",
    placement: "bottom",
    hidePrev: true,
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.CONTENT,
    content:
      "Here you can see your personal details including contact information and account status.",
    title: "Profile Details",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.SETTINGS_TAB,
    content:
      "This is where you can manage your account settings and preferences.",
    title: "Account Management",
    placement: "bottom",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
    action: {
      type: COMMON_ACTIONS.CLICK_TAB,
      selector: ".account-settings-tab-item",
      waitForContent: SELECTORS.PROFILE.SETTINGS_CONTENT,
    },
  }),
  createStep({
    target: SELECTORS.PROFILE.SETTINGS_CONTENT,
    content:
      "Here you can edit your profile information, change your password, and view library card.",
    title: "Account Settings",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.NOTIFICATIONS_TAB,
    content:
      "Click here to view your notifications, loan alerts, and fine notifications.",
    title: "Notifications",
    placement: "bottom",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
    action: {
      type: COMMON_ACTIONS.CLICK_TAB,
      selector: ".notifications-tab-item",
      waitFor: ".notifications-tab-item.active",
      waitForContent: SELECTORS.PROFILE.NOTIFICATIONS_CONTENT,
    },
  }),
  createStep({
    target: SELECTORS.PROFILE.NOTIFICATIONS_CONTENT,
    content:
      "This section shows all your notifications including loan due dates, fine alerts, and system announcements.",
    title: "Notifications Content",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.ACTIVITY_TAB,
    content:
      "Click here to view your library activity including current loans, requests, and transaction history.",
    title: "Your Activity",
    placement: "bottom",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
    action: {
      type: COMMON_ACTIONS.CLICK_TAB,
      selector: ".activity-tab-item",
      waitFor: ".activity-tab-item.active",
      waitForContent: '[data-tour="activity-content"]',
    },
  }),
  createStep({
    target: SELECTORS.PROFILE.ACTIVITY_OVERVIEW,
    content: "This overview shows a summary of your current library activity.",
    title: "Activity Overview",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.ACTIVITY_SUBTABS,
    content:
      "These subtabs let you switch between current and past library activities.",
    title: "Past and Present Activity",
    placement: "bottom",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.PROFILE.CURRENT_LOANS,
    content:
      "Here you can see all your currently borrowed books and their due dates.",
    title: "Current Loans",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.COMMON.FOOTER_FEEDBACK,
    content:
      "Have feedback about the system? You can report issues or suggestions here.",
    title: "Feedback Section",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),
  createStep({
    target: SELECTORS.COMMON.FOOTER_INFO,
    content:
      "Find additional information about the library, contact details, and policies.",
    title: "More Information",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.PROFILE,
  }),

  // Tour Completion
  createStep({
    target: SELECTORS.COMMON.BODY,
    content:
      "Congratulations! You've completed the LIMS tour. You now know how to use the dashboard, search for materials, and manage your profile. You can always access the tour from the help links below if you need them again. Thank you for taking the tour!",
    title: "Tour Complete!",
    placement: "top",
    route: ROUTES.PROFILE,
    section: SECTIONS.COMPLETION,
  }),
];

// Simplified function - only returns onboarding steps
export const getTourSteps = (tourType = TOUR_TYPES.ONBOARDING) => {
  return onboardingTourSteps;
};

// Helper functions
export const getTourProgress = (currentStepIndex) => {
  const totalSteps = onboardingTourSteps.length;
  const currentSection = onboardingTourSteps[currentStepIndex]?.section;

  return {
    overall: {
      current: currentStepIndex + 1,
      total: totalSteps,
      percentage: Math.round(((currentStepIndex + 1) / totalSteps) * 100),
    },
    section: currentSection,
  };
};

export const getNextRoute = (currentStepIndex) => {
  const currentStep = onboardingTourSteps[currentStepIndex];
  const nextStep = onboardingTourSteps[currentStepIndex + 1];

  if (!nextStep) return null;
  if (currentStep?.route !== nextStep?.route) {
    return nextStep.route;
  }
  return null;
};
